import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { collection, query, where, onSnapshot, doc, getDoc, setDoc } from 'firebase/firestore';
import { Html5Qrcode } from 'html5-qrcode';
import { db, auth } from '../firebase';
import { ownerStamp } from '../recordOwner';
import { ROLE, normaliseRole, getSubjectClaims } from '../roles';
import { Camera, X, IdCard } from 'lucide-react';
import QRCode from 'qrcode';
import { buildPayload, SUBJECT } from '../attendanceQr';
import { useT } from '../i18n.jsx';
import { DAY_HALF, DEFAULT_CUTOFF, halfOf, blockingRecord, attendanceDocId } from '../attendanceDay.js';
import '../theme-palette.css';

/**
 * My Attendance, student view.
 *
 * A separate module from the staff Attendance register, and deliberately the narrowest one in the
 * portal: scan a QR code, see your own records. There is no manual check-in -- the staff module has
 * one, but a student who can file attendance without scanning anything can mark themselves present
 * from home, which defeats the point of the code.
 *
 * The data layer agrees rather than merely going along with it: a member may only create a row
 * naming their own address and only with verified:false, and every edit stays with staff. So the
 * absence of a manual button here is a convenience, not the control.
 */
const QR_VALIDITY_MS = 5 * 60 * 1000;
const QR_REGION_ID = 'my-attendance-qr-reader';

const localDateString = (d) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const formatWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.toLocaleDateString('en-CA')} ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
};

const MyAttendanceUser = () => {
  const { activeSchoolId, isMobile, userProfile } = useOutletContext();
  const { t } = useT();

  const ownEmail = (auth.currentUser?.email || '').toLowerCase();
  const ownRole = normaliseRole(userProfile?.role);
  const isStudent = ownRole === ROLE.STUDENT;
  // The same code that is printed on the card, shown on screen instead. Not a second way in: it is
  // still a member of staff holding the scanner who writes the record, so a student showing this is
  // doing exactly what a student handing over a card does.
  const [cardQr, setCardQr] = useState('');
  const [cardMeta, setCardMeta] = useState(null);
  const isParent = ownRole === ROLE.PARENT;

  const ownSid = userProfile?.studentId || '';
  const ownName = userProfile?.name || ownSid || ownEmail;

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showScan, setShowScan] = useState(false);
  const [scanError, setScanError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [hasCamera, setHasCamera] = useState(true);

  const scannerRef = useRef(null);
  const handledRef = useRef(false);

  // A guardian views by child. Nothing is stamped onto the attendance rows to make this work: the
  // row already carries submittedBySid and the token already carries childSids, so the rule
  // compares the two directly. The picker is built from the same claim, which is why it can never
  // offer a child whose records would then be refused.
  const [claims, setClaims] = useState(null);
  const [selectedSid, setSelectedSid] = useState('');
  useEffect(() => { getSubjectClaims(auth.currentUser).then(setClaims); }, []);

  const children = useMemo(() => {
    if (!isParent || !claims) return [];
    const allowed = new Set(claims.childSids || []);
    return (userProfile?.children || [])
      .filter(c => c.sid && allowed.has(c.sid))
      .map(c => ({ sid: c.sid, label: `${c.currentClass || '-'} - ${c.name || c.sid} (${c.sid})` }));
  }, [isParent, claims, userProfile]);

  useEffect(() => {
    if (children.length && !children.some(c => c.sid === selectedSid)) setSelectedSid(children[0].sid);
  }, [children, selectedSid]);

  useEffect(() => {
    if (!navigator.mediaDevices?.enumerateDevices) { setHasCamera(false); return; }
    navigator.mediaDevices.enumerateDevices()
      .then(list => setHasCamera(list.some(d => d.kind === 'videoinput')))
      .catch(() => setHasCamera(false));
  }, []);

  // Scoped to submittedByEmail, which is the field the security rule tests. Anything broader would
  // be refused outright rather than returning fewer rows.
  useEffect(() => {
    if (!activeSchoolId || !ownEmail) return;
    if (isParent && !selectedSid) { setRecords([]); setLoading(false); return; }
    const q = isParent
      ? query(
          collection(db, 'schools', activeSchoolId, 'attendance_record'),
          where('submittedBySid', '==', selectedSid),
        )
      : query(
          collection(db, 'schools', activeSchoolId, 'attendance_record'),
          where('submittedByEmail', '==', ownEmail),
        );
    const unsub = onSnapshot(q, (snap) => {
      const list = [];
      snap.forEach(d => list.push({ id: d.id, ...d.data() }));
      list.sort((a, b) => String(b.submittedAt || '').localeCompare(String(a.submittedAt || '')));
      setRecords(list);
      setLoading(false);
    }, (e) => { console.error('Error loading my attendance', e); setLoading(false); });
    return () => unsub();
  }, [activeSchoolId, ownEmail, isParent, selectedSid]);

  // The school's boundary between the two marks. Read once; a failure leaves the default rather
  // than blocking a student from checking in at all.
  const [halfCutoff, setHalfCutoff] = useState(DEFAULT_CUTOFF);
  useEffect(() => {
    if (!activeSchoolId) return;
    getDoc(doc(db, 'schools', activeSchoolId, 'metadata', 'attendance_settings'))
      .then(snap => setHalfCutoff((snap.exists() && snap.data().halfCutoff) || DEFAULT_CUTOFF))
      .catch(e => console.error('Error loading attendance settings', e));
  }, [activeSchoolId]);

  const todayCount = useMemo(() => {
    const today = localDateString(new Date());
    return records.filter(r => r.submittedDateLocal === today).length;
  }, [records]);

  // --- scanner lifecycle --------------------------------------------------------
  useEffect(() => {
    if (!showScan) return;
    handledRef.current = false;
    setScanError('');
    const scanner = new Html5Qrcode(QR_REGION_ID);
    scannerRef.current = scanner;

    scanner.start(
      { facingMode: 'environment' },
      { fps: 10, qrbox: 250 },
      (decoded) => handleScan(decoded),
      () => { /* per-frame decode miss */ },
    ).catch(err => setScanError(t('myAttendance.cameraFailed', { message: err.message })));

    // html5-qrcode's stop() throws SYNCHRONOUSLY when the scanner is not running -- camera denied,
    // or a successful scan already stopped it. A trailing .catch() never runs, because the throw
    // happens before the promise exists, so this must be a plain try/catch guarded by isScanning or
    // it surfaces as an uncaught error that takes the page down.
    return () => {
      const s = scannerRef.current;
      if (!s) return;
      try {
        if (s.isScanning) {
          s.stop().then(() => { try { s.clear(); } catch (e) { /* no-op */ } }).catch(() => { /* no-op */ });
        } else {
          s.clear();
        }
      } catch (e) { /* already stopped, or never started */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showScan]);

  // Loaded by SID, which is the document key and is also on the token -- so this is one read and
  // the rule needs no lookup. A student with no card issued simply sees nothing here rather than an
  // error: not having been issued one yet is a normal state at the start of a year.
  useEffect(() => {
    if (!isStudent || !activeSchoolId || !ownSid) { setCardQr(''); return; }
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'schools', activeSchoolId, 'attendance_qr', ownSid));
        if (!snap.exists()) { if (!cancelled) setCardQr(''); return; }
        const rec = snap.data();
        const url = await QRCode.toDataURL(buildPayload({
          schoolId: activeSchoolId, subjectType: SUBJECT.STUDENT, id: ownSid, secret: rec.secret,
        }), { width: 300, margin: 1 });
        if (!cancelled) { setCardQr(url); setCardMeta(rec); }
      } catch (e) {
        // Nothing actionable for a student here, and the register below is the point of the screen.
        console.error('Could not load your attendance card', e);
      }
    })();
    return () => { cancelled = true; };
  }, [isStudent, activeSchoolId, ownSid]);

  const handleScan = async (decodedText) => {
    if (handledRef.current) return;
    handledRef.current = true;
    const scanner = scannerRef.current;
    try { if (scanner?.isScanning) await scanner.stop(); } catch (e) { /* already stopped */ }
    setShowScan(false);

    let payload;
    try {
      payload = JSON.parse(decodedText);
    } catch (e) {
      alert(t('myAttendance.qrNotValid'));
      return;
    }
    if (payload.s !== activeSchoolId) { alert(t('myAttendance.qrOtherSchool')); return; }
    if (!payload.id) { alert(t('myAttendance.qrNotValid')); return; }

    // The payload's own claims are not trusted. Freshness is checked against the qr_record document
    // written server-side when the code was generated, so a fabricated id with no matching document
    // is rejected outright -- that is the real "is this code genuine" test.
    let qrData;
    try {
      const snap = await getDoc(doc(db, 'schools', activeSchoolId, 'qr_record', payload.id));
      if (!snap.exists()) {
        alert(t('myAttendance.qrUnverified'));
        return;
      }
      qrData = snap.data();
    } catch (e) {
      console.error('Error verifying QR code', e);
      alert(t('myAttendance.qrCheckFailed'));
      return;
    }

    const generatedAt = new Date(qrData.generatedAt).getTime();
    if (Number.isNaN(generatedAt) || Date.now() - generatedAt > QR_VALIDITY_MS) {
      alert(t('myAttendance.qrExpired'));
      return;
    }

    await submitAttendance(payload.id);
  };

  const submitAttendance = async (qrId) => {
    const now0 = new Date();
    const half = halfOf(now0.toISOString(), halfCutoff);
    const clash = blockingRecord(
      records.filter(r => r.submittedDateLocal === localDateString(now0)),
      half,
    );
    if (clash) {
      alert(t(half === DAY_HALF.IN ? 'myAttendance.signedInAt' : 'myAttendance.signedOutAt', {
        time: formatWhen(clash.submittedAt).split(' ')[1] || '',
      }));
      return;
    }
    setSubmitting(true);
    try {
      let submitIp = 'Unknown';
      try {
        const res = await fetch('https://api.ipify.org?format=json');
        const data = await res.json();
        submitIp = data.ip || 'Unknown';
      } catch (e) {
        // Best-effort only.
      }

      const now = new Date();
      const payload = { ...ownerStamp(),
        session: half,
        submittedAt: now.toISOString(),
        submittedDateLocal: localDateString(now),
        // The rule requires this to equal the caller's own address, and refuses verified:true. A
        // student can record that they arrived; only staff can say it counts.
        submittedByEmail: ownEmail,
        submittedByInitial: '',
        submittedBySid: ownSid,
        submittedByName: ownName,
        submittedByRole: ROLE.STUDENT,
        role: 'Student',
        submitIp,
        method: 'QR',
        qrId,
        verified: false,
        schoolYear: userProfile?.schoolYear || '',
      };
      // A second check-in in the same half lands on the same document, and a student has no update
      // right -- so the rules refuse it even if this screen's own guard were bypassed.
      const id = attendanceDocId(payload);
      const ref = id
        ? doc(db, 'schools', activeSchoolId, 'attendance_record', id)
        : doc(collection(db, 'schools', activeSchoolId, 'attendance_record'));
      await setDoc(ref, payload);
      alert(t('myAttendance.checkedIn'));
    } catch (e) {
      console.error('Error submitting attendance', e);
      alert(t('myAttendance.checkInFailed', { message: e.message }));
    }
    setSubmitting(false);
  };

  const card = {
    backgroundColor: 'var(--color-bg-card)',
    borderRadius: isMobile ? 0 : 'var(--radius-lg)',
    boxShadow: isMobile ? 'none' : 'var(--shadow-sm)',
  };
  const th = { padding: '0.7rem 0.9rem', fontSize: '0.72rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--color-text-muted)', textAlign: 'left', whiteSpace: 'nowrap' };
  const td = { padding: '0.65rem 0.9rem', fontSize: '0.875rem', borderTop: '1px solid var(--color-border)' };

  return (
    <div className="animate-fade-in new-theme" style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* The screen used to open with a tabs bar whose only tab was a label with no onClick --
          the tab bar in the shell now names the screen, so all that is left here is the control
          that genuinely belongs to it. The row disappears entirely when there is nothing in it,
          which for a student is always. */}
      {isParent && children.length > 1 && (
        <div className="theme-tabs-bar" style={{ justifyContent: 'flex-end' }}>
          <select
            className="input-field"
            aria-label={t('family.child')}
            value={selectedSid}
            onChange={e => setSelectedSid(e.target.value)}
            style={{ margin: 0, width: 'auto', minWidth: '220px', maxWidth: '100%' }}
          >
            {children.map(c => <option key={c.sid} value={c.sid}>{c.label}</option>)}
          </select>
        </div>
      )}

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: isMobile ? '0.75rem' : '0 1.5rem 1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {isParent ? (
          <div style={{ ...card, padding: isMobile ? '1rem' : '1.25rem', fontSize: '0.85rem', color: 'var(--color-text-muted)', lineHeight: 1.6 }}>
            {children.length === 0
              ? t('family.noChildLinked')
              : t('myAttendance.parentViewIntro', {
                name: children.find(c => c.sid === selectedSid)?.label || t('myAttendance.parentViewYourChild'),
              })}
          </div>
        ) : (
        <div style={{ ...card, padding: isMobile ? '1rem' : '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.75rem', alignItems: 'flex-start' }}>
          <button
            className="btn-primary"
            onClick={() => setShowScan(true)}
            disabled={submitting || !hasCamera || !isStudent}
            title={!hasCamera ? t('myAttendance.noCameraShort') : undefined}
            style={{ width: 'auto', display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1.5rem' }}
          >
            <Camera size={18} /> {submitting ? t('myAttendance.scanning') : t('myAttendance.scanButton')}
          </button>

          <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', lineHeight: 1.6 }}>
            {!isStudent
              ? t('myAttendance.studentsOnly')
              : !hasCamera
                ? t('myAttendance.noCamera')
                : todayCount > 0
                  ? t('myAttendance.checkedInToday', { count: todayCount })
                  : t('myAttendance.scanIntro')}
          </span>
        </div>
        )}

        {isStudent && cardQr && (
          <div style={{ ...card, padding: isMobile ? '1rem' : '1.5rem', display: 'flex', gap: '1.1rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <img src={cardQr} alt={t('myAttendance.cardAlt')} style={{ width: 150, height: 150, background: '#fff', borderRadius: 'var(--radius-md)' }} />
            <div style={{ flex: '1 1 220px', minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.95rem', fontWeight: '700' }}>
                <IdCard size={17} /> {t('myAttendance.cardTitle')}
              </div>
              <p style={{ margin: '0.35rem 0 0', fontSize: '0.83rem', color: 'var(--color-text-muted)', lineHeight: 1.65 }}>
                {t('myAttendance.cardIntro')}
              </p>
              <p style={{ margin: '0.4rem 0 0', fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>
                {[cardMeta?.currentClass, cardMeta?.schoolYear].filter(Boolean).join(' · ')}
              </p>
            </div>
          </div>
        )}

        <div style={{ ...card, overflowX: 'auto' }}>
          {loading ? (
            <div style={{ padding: '2.5rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>{t('common.loading')}</div>
          ) : records.length === 0 ? (
            <div style={{ padding: '2.5rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
              {t('myAttendance.noRecords')}
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '520px' }}>
              <thead><tr style={{ backgroundColor: 'var(--color-bg-app)' }}>
                <th style={th}>{t('common.date')}</th><th style={th}>{t('common.time')}</th><th style={th}>{t('myAttendance.method')}</th><th style={th}>{t('common.status')}</th>
              </tr></thead>
              <tbody>
                {records.map(r => (
                  <tr key={r.id}>
                    <td style={td}>{r.submittedDateLocal || '-'}</td>
                    <td style={td}>{formatWhen(r.submittedAt).split(' ')[1] || '-'}</td>
                    <td style={td}>{r.method || '-'}</td>
                    <td style={{ ...td, color: r.verified ? 'var(--color-success-text)' : 'var(--color-text-muted)', fontWeight: r.verified ? '600' : '400' }}>
                      {r.verified ? t('myAttendance.confirmed') : t('myAttendance.awaitingConfirmation')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showScan && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 120, backgroundColor: 'var(--color-overlay-strong)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ backgroundColor: 'var(--color-bg-card)', borderRadius: 'var(--radius-lg)', width: '100%', maxWidth: '420px', overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.9rem 1.1rem', borderBottom: '1px solid var(--color-border)' }}>
              <strong style={{ fontSize: '1rem' }}>{t('myAttendance.scanToCheckIn')}</strong>
              <button type="button" onClick={() => setShowScan(false)} aria-label={t('common.close')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', display: 'flex' }}>
                <X size={20} />
              </button>
            </div>
            <div id={QR_REGION_ID} style={{ width: '100%' }} />
            {scanError && (
              <div style={{ padding: '0.9rem 1.1rem', color: 'var(--color-danger)', fontSize: '0.85rem' }}>{scanError}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MyAttendanceUser;
